import { forgotPasswordSchema, resetPasswordSchema } from "@cafe/shared";
import { Alert, Button, Card, Field, Input, PasswordInput, toast } from "@cafe/ui";
import { useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { ApiError, errorText, http, useSession } from "../lib/api";
import { CocaLogo } from "../logo";

function Shell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-bg p-4">
      <Card className="flex w-full max-w-md flex-col gap-5 p-6">
        <div>
          <h1 className="flex items-end gap-2"><CocaLogo className="h-10 w-auto" /><span className="pb-1 text-lg font-semibold text-muted">Platform</span></h1>
          <p className="mt-1 text-sm text-muted">{title}</p>
        </div>
        {children}
      </Card>
    </div>
  );
}

const backLink = <Link to="/login" className="text-center text-sm font-semibold text-primary hover:underline">Вернуться ко входу</Link>;

/** Сброс пароля сотрудника платформы, шаг 1: ссылка на email. Ответ одинаковый — не раскрываем, есть ли такой сотрудник. */
export function ForgotPasswordPage() {
  const session = useSession();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (session) return <Navigate to="/" replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = forgotPasswordSchema.safeParse({ email });
    if (!v.success) return setError(v.error.issues[0]?.message ?? "Некорректный email");
    setBusy(true);
    setError(null);
    try {
      await http.post("/auth/password/forgot", v.data);
      setSent(v.data.email);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Восстановление пароля">
      {sent ? (
        <>
          <Alert tone="success" title="Проверьте почту">Если {sent} — адрес сотрудника платформы, на него придёт ссылка. Она действует 30 минут.</Alert>
          <Button variant="outline" onClick={() => setSent(null)}>Запросить ссылку ещё раз</Button>
          {backLink}
        </>
      ) : (
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-5">
          <Field label="Email" error={error ?? undefined} hint={!error ? "Пришлём ссылку для нового пароля. Код из приложения на телефоне при входе останется прежним." : undefined} required>
            <Input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(null); }} autoComplete="username" autoFocus />
          </Field>
          <Button type="submit" size="lg" loading={busy} disabled={!email}>Отправить ссылку</Button>
          {backLink}
        </form>
      )}
    </Shell>
  );
}

/** Токен из ссылки письма: читаем и сразу убираем из адресной строки (не утечёт в Referer и историю). */
function takeToken() {
  const url = new URL(window.location.href);
  const token = url.searchParams.get("token") ?? "";
  if (token) {
    url.searchParams.delete("token");
    window.history.replaceState(null, "", url.pathname + url.search);
  }
  return token;
}

/** Шаг 2: новый пароль по ссылке; прежние входы завершаются. */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [token] = useState(takeToken);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(!token);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = resetPasswordSchema.safeParse({ token, password });
    if (!v.success) return setError(v.error.issues.find((i) => i.path[0] === "password")?.message ?? "Ссылка неполная — запросите новую");
    setBusy(true);
    setError(null);
    try {
      await http.post("/auth/password/reset", v.data);
      toast.success("Пароль изменён — войдите с новым паролем");
      navigate("/login", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.fields.some((f) => f.path === "token")) setExpired(true);
      else setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Новый пароль">
      {expired ? (
        <>
          <Alert tone="warning">Ссылка недействительна или устарела — запросите сброс ещё раз.</Alert>
          <Link to="/forgot-password" className="text-center text-sm font-semibold text-primary hover:underline">Запросить ссылку ещё раз</Link>
        </>
      ) : (
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-5">
          <Field label="Новый пароль" error={error ?? undefined} hint={!error ? "Минимум 10 символов, буквы и цифры" : undefined} required>
            <PasswordInput value={password} onChange={(e) => { setPassword(e.target.value); setError(null); }} autoComplete="new-password" autoFocus />
          </Field>
          <Button type="submit" size="lg" loading={busy} disabled={!password}>Сохранить пароль</Button>
          {backLink}
        </form>
      )}
    </Shell>
  );
}
